import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
    setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

interface ThemeProviderProps {
    children: ReactNode;
}

function getThemeKey(): string {
    try {
        const raw = localStorage.getItem('tg-auth-storage')
        if (raw) {
            const parsed = JSON.parse(raw)
            const userId = parsed?.state?.user?.id
            if (userId) return `tg-manager-theme-${userId}`
        }
    } catch { /* ignore */ }
    return 'tg-manager-theme'
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [theme, setThemeState] = useState<Theme>(() => {
        const key = getThemeKey()
        const saved = localStorage.getItem(key)
        // Default is dark
        return (saved as Theme) || 'dark';
    });

    useEffect(() => {
        const key = getThemeKey()
        localStorage.setItem(key, theme);
        // Legacy key for fallback
        localStorage.setItem('tg-manager-theme', theme);
        document.documentElement.setAttribute('data-theme', theme);

        if (theme === 'dark') {
            document.documentElement.classList.add('dark');
            document.documentElement.classList.remove('light');
        } else {
            document.documentElement.classList.add('light');
            document.documentElement.classList.remove('dark');
        }
    }, [theme]);

    const toggleTheme = () => {
        setThemeState(prev => prev === 'light' ? 'dark' : 'light');
    };

    const setTheme = (newTheme: Theme) => {
        setThemeState(newTheme);
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeProvider;
